import { User } from '../icc-api/model/User'
import { IccDeviceApi, IccPatientApi, IccUserApi } from '../icc-api'
import { HealthcareParty } from '../icc-api/model/HealthcareParty'
import { Patient } from '../icc-api/model/Patient'
import { Device } from '../icc-api/model/Device'
import { fingerprintV1, fingerprintV2, hexPublicKeysWithSha1Of, hexPublicKeysWithSha256Of } from './crypto/utils'
import { IccHcpartyXApi } from './icc-hcparty-x-api'
import { XHR } from '../icc-api/api/XHR'
import { CryptoActorStub, CryptoActorStubWithType } from '../icc-api/model/CryptoActorStub'
import { DataOwnerTypeEnum } from '../icc-api/model/DataOwnerTypeEnum'
import { AuthenticationProvider, NoAuthenticationProvider } from './auth/AuthenticationProvider'
import { IccDataownerApi } from '../icc-api/api/IccDataownerApi'
import { DataOwnerWithType } from '../icc-api/model/DataOwnerWithType'

export type DataOwnerOrStub = DataOwner | CryptoActorStub
export type DataOwner = HealthcareParty | Patient | Device

/**
 * Extended API for managing data owners (healthcare parties, patients, and devices).
 * Provides methods to retrieve data owner information, manage hierarchies, and handle cryptographic keys.
 */
export class IccDataOwnerXApi extends IccDataownerApi {
  private currentDataOwnerType: DataOwnerTypeEnum | undefined
  private currentDataOwnerHierarchyIds: string[] | undefined

  private get selfDataOwnerId(): string | undefined {
    if (!this.currentDataOwnerHierarchyIds) return undefined
    return this.currentDataOwnerHierarchyIds[this.currentDataOwnerHierarchyIds.length - 1]
  }

  constructor(
    host: string,
    headers: { [key: string]: string },
    authenticationProvider: AuthenticationProvider = new NoAuthenticationProvider(),
    fetchImpl: (input: RequestInfo, init?: RequestInit) => Promise<Response> = typeof window !== 'undefined'
      ? window.fetch
      : typeof self !== 'undefined'
      ? self.fetch
      : fetch
  ) {
    super(host, headers, authenticationProvider, fetchImpl)
  }

  /**
   * Gets the public keys of a data owner to be used for encryption with RSA-OAEP+SHA-1, in hex format.
   * @param dataOwner a data owner.
   * @return the public keys for the data owner in hex format.
   */
  getHexPublicKeysWithSha1Of(dataOwner: DataOwnerOrStub): Set<string> {
    return hexPublicKeysWithSha1Of(dataOwner)
  }

  /**
   * Gets the public keys of a data owner to be used for encryption with RSA-OAEP+SHA-256, in hex format.
   * @param dataOwner a data owner.
   * @return the public keys for the data owner in hex format.
   */
  getHexPublicKeysWithSha256Of(dataOwner: DataOwnerOrStub): Set<string> {
    return hexPublicKeysWithSha256Of(dataOwner)
  }

  /**
   * Gets all public keys of a data owner to be used for encryption, in hex format. Note that this may include keys that should be used for different
   * encryption algorithms.
   */
  getHexPublicKeysOf(dataOwner: DataOwnerOrStub): Set<string> {
    return new Set([...this.getHexPublicKeysWithSha1Of(dataOwner), ...this.getHexPublicKeysWithSha256Of(dataOwner)])
  }

  /**
   * Gets the crypto actor stub for the current data owner. This is a lightweight representation containing only
   * cryptographic information without full data owner details.
   * @return the crypto actor stub of the current data owner.
   * @throws if the current user is not a data owner.
   */
  async getCurrentDataOwnerStub(): Promise<CryptoActorStubWithType> {
    return CryptoActorStubWithType.fromDataOwner(await this.getCurrentDataOwner())
  }

  /**
   * If the logged user is a data owner get the current data owner. This information is cached without expiration, and will only be updated in case
   * of forced refresh.
   * @throws if the current user is not a data owner.
   */
  async getCurrentDataOwnerId(): Promise<string> {
    if (!this.currentDataOwnerHierarchyIds) {
      await this.getCurrentDataOwnerHierarchyIds()
    }
    return this.currentDataOwnerHierarchyIds![this.currentDataOwnerHierarchyIds!.length - 1]
  }

  /**
   * If the logged user is a data owner get its parent hierarchy. This information is cached without expiration, and will only be updated in case
   * of forced refresh.
   * The resulting array starts with the topmost parent (the only ancestor without a parent) and ends with the data owner itself.
   * @throws if the current user is not a data owner.
   */
  async getCurrentDataOwnerHierarchyIds(): Promise<string[]> {
    if (!this.currentDataOwnerHierarchyIds) {
      await this.forceLoadCurrentDataOwnerHierarchyAndCacheIds()
    }
    return [...this.currentDataOwnerHierarchyIds!]
  }

  /**
   * Get the hierarchy for the current data owner starting from the specified parent.
   * @param parentId the id of a parent in the current data owner hierarchy.
   * @return an array starting at the topmost parent and ending at the provided parent id.
   * @throws if the provided id is not part of the data owner hierarchy.
   */
  async getCurrentDataOwnerHierarchyIdsFrom(parentId: string): Promise<string[]> {
    if (!this.currentDataOwnerHierarchyIds) {
      await this.getCurrentDataOwnerHierarchyIds()
    }
    const res = []
    for (const dataOwnerId of this.currentDataOwnerHierarchyIds!) {
      res.push(dataOwnerId)
      if (dataOwnerId === parentId) return res
    }
    throw new Error(`${parentId} is not part of the data owner hierarchy for the current user`)
  }

  /**
   * If the logged user is a data owner get the current data owner and all of his parents.
   * @throws if the current user is not a data owner.
   * @return the current data owner hierarchy, starting from the topmost parent to the current data owner.
   */
  async getCurrentDataOwnerHierarchy(): Promise<DataOwnerWithType[]> {
    if (!this.currentDataOwnerHierarchyIds) {
      return await this.forceLoadCurrentDataOwnerHierarchyAndCacheIds()
    } else return Promise.all(this.currentDataOwnerHierarchyIds!.map((id) => this.getDataOwner(id)))
  }

  /**
   * If the logged user is a data owner get the type of the current data owner. This information is cached.
   * @throws if the current user is not a data owner
   */
  async getCurrentDataOwnerType(): Promise<DataOwnerTypeEnum> {
    if (!this.currentDataOwnerHierarchyIds) {
      await this.forceLoadCurrentDataOwnerHierarchyAndCacheIds()
    }
    return this.currentDataOwnerType!
  }

  /**
   * Get the id of the data owner entity for a user.
   * @param user a user that is also a data owner.
   * @return the id of the data owner corresponding to the provided user.
   * @throws if the user is not a data owner.
   */
  getDataOwnerIdOf(user: User): string {
    const dataOwnerId = user.healthcarePartyId ?? user.patientId ?? user.deviceId
    if (dataOwnerId == undefined) {
      throw Error(`User ${user.id} is not a data owner`)
    }
    return dataOwnerId
  }

  /**
   * Get a data owner. Note that this does not decrypt patient data owners.
   * @param ownerId id of the data owner to retrieve (patient, medical device, hcp, ...)
   * @return the data owner with the provided id
   * @throws if you have no access to the data owner. Use {@link getCryptoActorStub}.
   */
  async getDataOwner(ownerId: string): Promise<DataOwnerWithType> {
    return super.getDataOwner(ownerId).then((dowt) => {
      if (dowt.dataOwner.id === this.selfDataOwnerId) this.checkDataOwnerIntegrity(dowt.dataOwner)
      return dowt
    })
  }

  /**
   * Get a crypto actor stub for a data owner.
   * @param ownerId id of the data owner for which you want to retrieve the stub (patient, medical device, hcp, ...)
   * @return the crypto actor stub of the data owner with the provided id
   */
  async getCryptoActorStub(ownerId: string): Promise<CryptoActorStubWithType> {
    return super.getCryptoActorStub(ownerId).then((dowt) => {
      if (dowt.stub.id === this.selfDataOwnerId) this.checkDataOwnerIntegrity(dowt.stub)
      return dowt
    })
  }

  /**
   * Clears the cache of current data owner id and parent hierarchy ids. The hierarchy of a data owner should not normally change over time, so this
   * method should be rarely needed.
   */
  clearCurrentDataOwnerIdsCache() {
    this.currentDataOwnerHierarchyIds = undefined
  }

  private checkDataOwnerIntegrity(dataOwner: DataOwnerOrStub) {
    const keys = new Set([...this.getHexPublicKeysWithSha1Of(dataOwner), ...this.getHexPublicKeysWithSha256Of(dataOwner)])
    if (new Set(Array.from(keys).map((x) => fingerprintV2(x))).size != keys.size)
      throw new Error(
        `Different public keys for ${dataOwner.id} have the same fingerprint; this should not happen in normal circumstances. Please report this error to iCure.`
      )
  }

  private async forceLoadCurrentDataOwnerHierarchyAndCacheIds(): Promise<DataOwnerWithType[]> {
    let curr = await this.getCurrentDataOwner()
    this.checkDataOwnerIntegrity(curr.dataOwner)
    this.currentDataOwnerType = curr.type
    let res = [curr]
    while ((curr.dataOwner as HealthcareParty).parentId) {
      curr = await this.getDataOwner((curr.dataOwner as HealthcareParty).parentId!)
      res = [curr, ...res]
    }
    this.currentDataOwnerHierarchyIds = res.map((x) => x.dataOwner.id!)
    return res
  }
}
